// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import React from 'react'
import { array, bool, func, object, oneOfType, string } from 'prop-types'
import THead from './THead'
import TBody from './TBody'
import GenericTooltipTrigger from './GenericTooltipTrigger'
import GenericTooltipWrapper from './GenericTooltipWrapper'
import ReactTooltip from 'react-tooltip'

// Read ./info.txt before digging in to this component for the first time.

class DataTable extends React.Component {
  static displayName = 'DataTable'

  static propTypes = {
    className: string,
    columns: array.isRequired,
    customProps: object,
    data: array.isRequired,
    handleSort: func,
    idRoot: string.isRequired,
    noResults: bool,
    rowClass: oneOfType([
      func,
      string,
    ]),
    rowClick: func,
    tableSort: object,
    unitType: string,
  }

  state = {
    tooltipColumn: null,
    TooltipComponent: null,
    tooltipData: null,
    tooltipId: null,
  }

  componentWillMount () {
    // if we have any TooltipComponents defined in the columns prop,
    // then configure the tooltip container and trigger
    if (this.props.columns.some(d => d.cellTooltip !== undefined)) {
      this.configureTooltip()
    }
  }

  componentDidUpdate (oldProps) {
    // if we got some new columns, then be sure to configure the shared tooltip accordingly.
    if (oldProps.columns !== this.props.columns &&
        this.props.columns.some(d => d.cellTooltip !== undefined)
    ) {
      this.configureTooltip()
    }
    // If there's a tooltipContainer defined in the columns then
    // this.state.tooltipId will be a string and not null.
    // if that's true and the data prop has changed, (either updated by the search or a sorting on the results)
    // in either case, we need to rebuild the tooltip so it will properly assign its triggers' DOM nodes
    if (this.state.tooltipId && this.props.data !== oldProps.data) {
      ReactTooltip.rebuild()
    }
  }

  // NOTE - if you need multiple tooltips for a single column, you'll need to
  // wrap the tooltip components in a shared parent component
  // and then specify which to use via the tooltipData
  assignTooltip = (tooltipColumn, tooltipData) => {
    // find if the column which trigger the tooltip is mapped in the columns.
    const mappedColumn = this.props.columns.find(d => d.columnKey === tooltipColumn)

    // if it was, assign that, and if it wasn't,
    // then assign null so the <TooltipWrapper /> assigns a warning.
    // to show that an unmapped column has triggered the shared tooltip.
    // NOTE - check for mappedColumn verifies that the column was defined in the columns prop
    // and mappedColumn.tooltipComponent checks to see if the specified column has a tooltipComponent
    const TooltipComponent = (
      mappedColumn && mappedColumn.cellTooltip
        ? mappedColumn.cellTooltip
        : null
    )

    this.setState(() => ({
      tooltipColumn,
      TooltipComponent,
      tooltipData,
    }))
  }

  // this handles the configuration of the shared tooltip
  configureTooltip = () => {
    // in case we are gonna use the shared tooltip wrapper,
    // define an id for it so we can pass that down to the table as well.
    // NOTE - this value should be dynamically generated as there can be multiple
    // <DataTable />s rendered at once.
    const tooltipId = `${this.props.idRoot}-generic-tooltip`
    this.setState(() => ({ tooltipId }))

    // asign this.TooltipTrigger to be a trigger component
    // which talks to the shared <DataTable /> tooltip.
    this.TooltipTrigger = props => (
      <GenericTooltipTrigger
        {...props}
        assignTooltip={this.assignTooltip}
        // idRoot={this.props.idRoot}
        tooltipId={tooltipId} />
    )

    // this value is by default.
    // Update this so that the <ReactTooltip /> gets mounted
    this.TooltipWrapper = () => <GenericTooltipWrapper {...this.state} />
  }

  // if the parent says to, display that there are no results to display.
  // NOTE - this was build with the assumption that the <Datatable />
  // would be rendering <SearchAndSelect /> results.
  // if you need to display a different message here, you'll need to wire in
  // a custom component as the noResults prop.
  noResultsMessage = () => (
    this.props.noResults
      ? <p className={'no-results-message'}>{'There are no results to display.'}</p>
      : null
  )

  // if no tooltipContainer is defined in the columns,
  // then there's no need to define these values as a react components
  // this also allows for tighter control over <DataTable />'s handling
  // of the shared tooltip
  TooltipWrapper = () => null
  TooltipTrigger = () => null

  render () {
    const {
      noResultsMessage,
      TooltipTrigger,
      TooltipWrapper,
    } = this

    const {
      className,
      columns,
      customProps,
      data,
      idRoot,
      rowClass,
      rowClick,
      handleSort,
      tableSort,
      unitType,
    } = this.props

    return (
      <div className={`data-table-wrapper ${className || ``}`}>
        <div className={`bg-grey-dark`}>
          <table
            className={`data-table bg-grey width-100`}
            id={idRoot}>
            <THead
              columns={columns}
              customProps={customProps}
              handleSort={handleSort}
              idRoot={`${idRoot}-header`}
              tableSort={tableSort}
              unitType={unitType} />
            <TBody
              columns={columns}
              customProps={customProps}
              data={data}
              idRoot={`${idRoot}-data`}
              rowClass={rowClass}
              rowClick={rowClick}
              TooltipTrigger={TooltipTrigger}
              unitType={unitType} />
          </table>
          <TooltipWrapper />
        </div>
        {noResultsMessage()}
      </div>
    )
  }
}

export default DataTable

// Cell formatters used application wide
export {
  FormatEpochToYYYYMMDD,
  FormatEpochToFullTimestamp,
  FormatStatus,
} from './Formatters'
